import { LanguageCodes } from '@onevesthq/ov-enums';
import type { ModelPortfolioData } from '../workflowCompletion/subSteps/createModelPortfolio/createModelPortfolio.types';

const FIELDS_TO_COMPARE = [
  'themeId',
  'theme',
  'translatedName',
  'translatedPortfolioDescription',
  'translatedInvestmentObjective',
  'translatedLiquidity',
  'translatedPortfolioDisclaimer',
  'riskReturnLevel',
  'riskProfileName',
  'securities',
  'strategy',
  'assetClassLevel',
  'assetClassBranches',
  'children',
  'suggestable',
  'zeroPercentCash',
  'lowThresholdScore',
  'suggestedMinScore',
  'suggestedMaxScore',
  'forecastedRateOfReturn',
  'forecastedStandardDeviation',
  'targetAllocations',
  'translatedUrl',
] as const;

let lastComparison: {
  currentValues: any;
  originalValues: any;
  result: boolean;
} | null = null;

const NUMERIC_FIELDS = new Set([
  'riskReturnLevel',
  'lowThresholdScore',
  'suggestedMinScore',
  'suggestedMaxScore',
  'forecastedRateOfReturn',
  'forecastedStandardDeviation',
]);

const EMPTY_HTML_PATTERNS = new Set([
  '',
  '<p><br></p>',
  '<p><br/></p>',
  '<p></p>',
]);

const isThemeObject = (val: unknown): val is { id?: string } =>
  typeof val === 'object' && val !== null && 'id' in val;

const normalizeValue = (value: any, field?: string): any => {
  if (value === null || value === undefined) {
    return null;
  }

  // Normalize numeric fields: NaN/empty equivalent to null, round to avoid float precision issues
  if (field && NUMERIC_FIELDS.has(field)) {
    const num = typeof value === 'number' ? value : Number.parseFloat(value);
    if (Number.isNaN(num) || value === '') return null;
    return Math.round(num * 100) / 100;
  }

  // Theme (Model Family): compare only id - API returns extra fields (core, description, iconKey, name)
  if (field === 'theme') {
    const id = isThemeObject(value) ? value.id : undefined;
    return id ?? null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (EMPTY_HTML_PATTERNS.has(trimmed)) {
      return null;
    }
    return trimmed;
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length === 0 ? null : value;
    }

    // Handle TranslatedString objects - normalize null/undefined/'' for consistent comparison
    const hasTranslatedKeys = Object.values(LanguageCodes).some(
      (lang) => lang in value,
    );
    if (hasTranslatedKeys) {
      const hasContent = Object.values(LanguageCodes).some((lang) => {
        const text = value[lang]?.trim();
        return text && !EMPTY_HTML_PATTERNS.has(text);
      });
      if (!hasContent) return null;
      // Normalize so null, undefined, '' are equivalent (API may return null, form uses '')
      const normalized: Record<string, string> = {};
      Object.values(LanguageCodes).forEach((lang) => {
        const v = value[lang];
        const str = v == null || typeof v !== 'string' ? '' : v;
        const trimmed = str.trim();
        const isEmpty = EMPTY_HTML_PATTERNS.has(trimmed);
        normalized[lang] = isEmpty ? '' : trimmed;
      });
      return normalized;
    }

    const keys = Object.keys(value);
    if (keys.length === 0) {
      return null;
    }

    // Recursively normalize nested objects
    const normalizedObj: Record<string, any> = {};
    let hasNonEmptyValues = false;

    keys.forEach((key) => {
      if (key === '__typename') return;
      const normalized = normalizeValue(value[key]);
      if (normalized !== null) {
        normalizedObj[key] = normalized;
        hasNonEmptyValues = true;
      }
    });

    return hasNonEmptyValues ? normalizedObj : null;
  }

  return value;
};

export const hasModelPortfolioChanges = (
  currentValues: Partial<ModelPortfolioData> | null,
  originalValues: Partial<ModelPortfolioData>,
): boolean => {
  if (
    lastComparison?.currentValues === currentValues &&
    lastComparison?.originalValues === originalValues
  ) {
    return lastComparison.result;
  }

  if (!currentValues) {
    lastComparison = { currentValues, originalValues, result: false };
    return false;
  }

  if (currentValues === originalValues) {
    lastComparison = { currentValues, originalValues, result: false };
    return false;
  }

  // Normalize themeId from theme.id for consistent comparison (form sets theme, API may have themeId)
  const withThemeId = (data: Partial<ModelPortfolioData>) => ({
    ...data,
    themeId: data.themeId ?? (data.theme as { id?: string })?.id,
  });
  const current = withThemeId(currentValues);
  const original = withThemeId(originalValues);

  const result = FIELDS_TO_COMPARE.some((field) => {
    const currentValue = normalizeValue(
      current[field as keyof ModelPortfolioData],
      field,
    );
    const originalValue = normalizeValue(
      original[field as keyof ModelPortfolioData],
      field,
    );

    if (currentValue === originalValue) {
      return false;
    }

    if (currentValue === null || originalValue === null) {
      return currentValue !== originalValue;
    }

    if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
      if (
        field === ('securities' as keyof ModelPortfolioData) ||
        field === ('children' as keyof ModelPortfolioData)
      ) {
        if (currentValue.length !== originalValue.length) return true;
        return currentValue.some((item, index) => {
          const otherItem = originalValue[index];
          if (!item || !otherItem) return true;
          if ('financialProduct' in item && 'financialProduct' in otherItem) {
            return (
              item.id !== otherItem.id ||
              item.percentage !== otherItem.percentage
            );
          }
          return false;
        });
      }
      if (field === ('targetAllocations' as keyof ModelPortfolioData)) {
        const deletedAllocations = originalValue.filter(
          (originalItem) =>
            !currentValue.some(
              (currentItem) =>
                currentItem.secondaryAssetClass.id ===
                originalItem.secondaryAssetClass.id,
            ),
        );

        const hasChangedAllocations = originalValue.some((item) => {
          const currentItem = currentValue.find(
            (i) => i.secondaryAssetClass.id === item.secondaryAssetClass.id,
          );
          if (!currentItem) return false;
          return currentItem.min !== item.min || currentItem.max !== item.max;
        });

        const newAllocations = currentValue.filter(
          (currentItem) =>
            !originalValue.some(
              (originalItem) =>
                originalItem.secondaryAssetClass.id ===
                currentItem.secondaryAssetClass.id,
            ),
        );
        const hasNewAllocations = newAllocations.length > 0;

        return (
          deletedAllocations.length > 0 ||
          hasChangedAllocations ||
          hasNewAllocations
        );
      }
      if (currentValue.length !== originalValue.length) return true;
      return currentValue.some((item, index) => {
        const otherItem = originalValue[index];
        return item !== otherItem;
      });
    }

    if (typeof currentValue === 'object' && typeof originalValue === 'object') {
      const currentKeys = Object.keys(currentValue);
      const originalKeys = Object.keys(originalValue);

      if (currentKeys.length !== originalKeys.length) {
        return true;
      }

      return currentKeys.some((key) => {
        const currentVal = (currentValue as Record<string, any>)[key];
        const originalVal = (originalValue as Record<string, any>)[key];
        return currentVal !== originalVal;
      });
    }

    return currentValue !== originalValue;
  });

  lastComparison = { currentValues, originalValues, result };
  return result;
};
